import React, { useState, useEffect, useCallback } from "react"
import PropTypes from 'prop-types'
import { Link } from "react-router-dom"
import { Dropdown, DropdownToggle, DropdownMenu, Row, Col } from "reactstrap"
import SimpleBar from "simplebar-react"
import api from "../../../utils/api"

import { withTranslation } from "react-i18next"

const typeIcons = {
  pending_request: { icon: 'mdi mdi-clock-outline', color: 'warning' },
  approved: { icon: 'mdi mdi-check-circle', color: 'success' },
  rejected: { icon: 'mdi mdi-close-circle', color: 'danger' },
  partially_approved: { icon: 'mdi mdi-check-all', color: 'info' },
}

const NotificationDropdown = props => {
  const [menu, setMenu] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count')
      setUnreadCount(res.data.count || 0)
    } catch (e) {
      // silent
    }
  }, [])

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications', { params: { per_page: 10 } })
      setNotifications(res.data.data || [])
    } catch (e) {
      // silent
    }
  }, [])

  // تحديث العدد كل 30 ثانية
  useEffect(() => {
    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [fetchUnreadCount])

  // جلب الإشعارات عند فتح القائمة
  useEffect(() => {
    if (menu) {
      fetchNotifications()
    }
  }, [menu, fetchNotifications])

  const handleMarkAsRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`)
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (e) {
      // silent
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/read-all')
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      setUnreadCount(0)
    } catch (e) {
      // silent
    }
  }

  const getNotifLink = (notif) => {
    const role = localStorage.getItem('role')
    if (notif.type === 'pending_request' && role === 'admin') {
      return notif.data?.pending_change_id
        ? `/dashboard/pending-approvals/${notif.data.pending_change_id}`
        : '/dashboard/pending-approvals'
    }
    return '/dashboard/my-requests'
  }

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'الآن'
    if (mins < 60) return `منذ ${mins} دقيقة`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `منذ ${hours} ساعة`
    const days = Math.floor(hours / 24)
    return `منذ ${days} يوم`
  }

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="dropdown d-inline-block ms-1"
        tag="li"
      >
        <DropdownToggle
          className="btn header-item noti-icon waves-effect"
          tag="button"
          id="page-header-notifications-dropdown"
        >
          <i className="ti-bell"></i>
          {unreadCount > 0 && (
            <span className="badge text-bg-danger rounded-pill">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0">
          <div className="p-3">
            <Row className="align-items-center">
              <Col>
                <h5 className="m-0">الإشعارات {unreadCount > 0 && `(${unreadCount})`}</h5>
              </Col>
              {unreadCount > 0 && (
                <Col className="text-end">
                  <button className="btn btn-link btn-sm p-0" onClick={handleMarkAllRead}>
                    قراءة الكل
                  </button>
                </Col>
              )}
            </Row>
          </div>

          <SimpleBar style={{ height: "230px" }}>
            {notifications.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="mdi mdi-bell-off-outline font-size-24 d-block mb-2"></i>
                لا توجد إشعارات
              </div>
            ) : (
              notifications.map(notif => {
                const typeInfo = typeIcons[notif.type] || typeIcons.pending_request
                return (
                  <Link
                    key={notif.id}
                    to={getNotifLink(notif)}
                    className="text-reset notification-item"
                    onClick={() => { if (!notif.is_read) handleMarkAsRead(notif.id); setMenu(false); }}
                    style={{ backgroundColor: notif.is_read ? 'transparent' : 'rgba(var(--bs-primary-rgb), 0.05)' }}
                  >
                    <div className="d-flex">
                      <div className="flex-shrink-0 me-3">
                        <div className="avatar-xs me-3">
                          <span className={`avatar-title border-${typeInfo.color} rounded-circle`}>
                            <i className={typeInfo.icon}></i>
                          </span>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mt-0 mb-1" style={{ fontWeight: notif.is_read ? 'normal' : 'bold' }}>
                          {notif.title}
                        </h6>
                        <div className="text-muted">
                          <p className="mb-1" style={{ fontSize: '0.8rem' }}>{notif.message}</p>
                          <p className="mb-0 font-size-12 text-muted">
                            <i className="mdi mdi-clock-outline me-1"></i>
                            {timeAgo(notif.created_at)}
                          </p>
                        </div>
                      </div>
                      {!notif.is_read && (
                        <div className="flex-shrink-0 align-self-center">
                          <span className="badge bg-primary rounded-pill" style={{width:8,height:8,padding:0}}>&nbsp;</span>
                        </div>
                      )}
                    </div>
                  </Link>
                )
              })
            )}
          </SimpleBar>
          <div className="p-2 border-top d-grid">
            <Link
              className="btn btn-sm btn-link font-size-14 btn-block text-center"
              to="/dashboard/my-requests"
              onClick={() => setMenu(false)}
            >
              <i className="mdi mdi-arrow-right-circle me-1"></i>
              عرض الكل
            </Link>
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  )
}

export default withTranslation()(NotificationDropdown)

NotificationDropdown.propTypes = {
  t: PropTypes.any
}